import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ChevronDown, X } from "lucide-react";
import { cn } from "@/utils/cn";
import { DashboardRoutes } from "@/modules/dashboard";
import { useFilteredNavItems } from "@/hooks/useFilteredNavItems";
import type { INavItem } from "@/models/INavItem";

interface ISidebarProps {
  collapsed: boolean;
  isOpen: boolean;
  isMobile: boolean;
  onToggle: () => void;
  onClose: () => void;
}

function NavItemLink({
  item,
  collapsed,
  isMobile,
  onClose,
}: {
  item: INavItem;
  collapsed: boolean;
  isMobile: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const label = t(item.labelKey);
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      end={item.path === DashboardRoutes.INDEX}
      onClick={isMobile ? onClose : undefined}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
          "text-white/70 hover:text-white hover:bg-white/10",
          isActive && "bg-white/20 text-white",
          collapsed && !isMobile && "justify-center px-2"
        )
      }
      title={collapsed && !isMobile ? label : undefined}
    >
      <Icon size={20} className="flex-shrink-0" />
      {(!collapsed || isMobile) && <span>{label}</span>}
    </NavLink>
  );
}

function NavItemWithChildren({
  item,
  collapsed,
  isMobile,
  onClose,
}: {
  item: INavItem;
  collapsed: boolean;
  isMobile: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(() => {
    // Start expanded if any child is active
    return item.children?.some((child) =>
      location.pathname === child.path || location.pathname.startsWith(child.path + "/")
    ) ?? false;
  });

  const label = t(item.labelKey);
  const Icon = item.icon;
  const hasActiveChild = item.children?.some((child) =>
    location.pathname === child.path || location.pathname.startsWith(child.path + "/")
  );

  // When collapsed on desktop, show as a simple link to the first child
  if (collapsed && !isMobile) {
    return (
      <NavLink
        to={item.path}
        className={cn(
          "flex items-center justify-center px-2 py-2.5 rounded-lg text-sm font-medium transition-colors",
          "text-white/70 hover:text-white hover:bg-white/10",
          hasActiveChild && "bg-white/20 text-white"
        )}
        title={label}
      >
        <Icon size={20} className="flex-shrink-0" />
      </NavLink>
    );
  }

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
          "text-white/70 hover:text-white hover:bg-white/10",
          hasActiveChild && "text-white"
        )}
      >
        <Icon size={20} className="flex-shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          size={16}
          className={cn(
            "transition-transform duration-200",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {isExpanded && (
        <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
          {item.children?.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              end
              onClick={isMobile ? onClose : undefined}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                  "text-white/60 hover:text-white hover:bg-white/10",
                  isActive && "bg-white/15 text-white"
                )
              }
            >
              <span>{t(child.labelKey)}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ collapsed, isOpen, isMobile, onToggle, onClose }: ISidebarProps) {
  const { t } = useTranslation();
  const { filteredNavItems } = useFilteredNavItems();

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen flex flex-col bg-ticketon-nav transition-all duration-300",
        // Desktop styles
        !isMobile && (collapsed ? "w-16" : "w-64"),
        // Mobile styles
        isMobile && "w-64",
        isMobile && (isOpen ? "translate-x-0" : "-translate-x-full")
      )}
    >
      <div className="flex h-16 items-center justify-between px-4 bg-white border-b border-gray-200">
        <img
          src={collapsed && !isMobile ? "/images/ticketon-o.svg" : "/images/ticketon-travel-logo.svg"}
          alt="Ticketon Travel"
          className={cn(
            "h-8 w-auto transition-all duration-300",
            collapsed && !isMobile ? "mx-auto" : ""
          )}
        />
        {isMobile ? (
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-ticketon-nav hover:bg-ticketon-nav/10 transition-colors"
            aria-label={t("sidebar.close")}
          >
            <X size={20} />
          </button>
        ) : !collapsed && (
          <button
            onClick={onToggle}
            className="p-2 rounded-lg border border-ticketon-nav text-ticketon-nav hover:bg-ticketon-nav/10 transition-colors"
            aria-label={t("sidebar.collapse")}
          >
            <ChevronLeft size={20} />
          </button>
        )}
      </div>
      {/* Expand button when collapsed */}
      {collapsed && !isMobile && (
        <button
          onClick={onToggle}
          className="w-full flex justify-center py-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label={t("sidebar.expand")}
        >
          <ChevronRight size={20} />
        </button>
      )}

      <nav className="flex-1 flex flex-col gap-1 p-2 mt-2 overflow-y-auto">
        {filteredNavItems.map((item) => {
          if (item.children && item.children.length > 0) {
            return (
              <NavItemWithChildren
                key={item.labelKey}
                item={item}
                collapsed={collapsed}
                isMobile={isMobile}
                onClose={onClose}
              />
            );
          }
          return (
            <NavItemLink
              key={item.path}
              item={item}
              collapsed={collapsed}
              isMobile={isMobile}
              onClose={onClose}
            />
          );
        })}
      </nav>
    </aside>
  );
}
